import { Form, message, Modal } from "antd";
import { Rule } from "antd/lib/form";
import { newsCategoryApi } from "api/newsCategory.api";
import { AppButton, AppInput } from "components/antd-ui";
import { SingleImageUpload } from "components/Upload/SingleImageUpload";
import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import { ModalStatus } from "types/modal";
import { News } from "types/news";

const rules: Rule[] = [{ required: true }];

interface NewsForm extends News {
  areaId: number;
  iconUrl: string;
}

export const NewsCategoryModal = observer(
  ({
    visible,
    status,
    news,
    onClose,
    onSubmitOk,
  }: {
    visible: boolean;
    status: ModalStatus;
    news: Partial<News>;
    onClose: () => void;
    onSubmitOk: () => void;
  }) => {
    const [form] = Form.useForm<NewsForm>();
    const [loading, setLoading] = useState(false);
    const [content, setContent] = useState("");
    const editorRef = useRef<{ setContent: (content: string) => void }>(null);

    useEffect(() => {
      if (status == "create" && visible) {
        form.resetFields();
      }
      setContent(news.content || "");
    }, [visible, status]);

    useEffect(() => {
      form.setFieldsValue({ ...news, areaId: news.area?.id });
      return () => {};
    }, [news]);

    const createData = async () => {
      const valid = await form.validateFields();
      const data = {
        newsCategory: {
          ...form.getFieldsValue(),
        },
      };

      setLoading(true);
      try {
        const res = await newsCategoryApi.create(data);
        message.success("Tạo mới thành công!");
        onClose();
        onSubmitOk();
      } finally {
        setLoading(false);
      }
    };

    const updateData = async () => {
      const valid = await form.validateFields();

      const data = {
        newsCategory: {
          ...form.getFieldsValue(),
        },
      };

      setLoading(true);
      try {
        const res = await newsCategoryApi.update(news?.id || 0, data);
        message.success("Cập nhật thành công");
        onClose();
        onSubmitOk();
      } finally {
        setLoading(false);
      }
    };

    const handleSubmit = async () => {
      if (status === "create") {
        await createData();
        return;
      }

      await updateData();
    };

    return (
      <Modal
        style={{ top: 15 }}
        onCancel={onClose}
        open={visible}
        title={
          <h1 className="text-xl font-bold text-center">
            {status === "create" ? "Tạo mới danh mục" : "Cập nhật danh mục"}
          </h1>
        }
        footer={null}
        className="app-form-modal"
      >
        <div className="app-form-modal__body">
          <section className="app-form-modal__section">
            <Form layout="vertical" form={form}>
              <Form.Item label="Tên danh mục" name="name" rules={rules}>
                <AppInput placeholder="Nhập tên danh mục" />
              </Form.Item>

              <Form.Item shouldUpdate={true} style={{ marginBottom: 0 }}>
                {() => {
                  return (
                    <Form.Item label="Icon" name="iconUrl">
                      <SingleImageUpload
                        width={100}
                        height={100}
                        onUploadOk={(path: string) => {
                          form.setFieldsValue({
                            iconUrl: path,
                          });
                        }}
                        imageUrl={form.getFieldValue("iconUrl")}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Form>
          </section>
        </div>
        <div className="ant-modal-footer">
          <div className="flex gap-2 justify-end">
            <AppButton onClick={onClose}>Hủy</AppButton>
            <AppButton tone="primary" loading={loading} onClick={handleSubmit}>
              {status === "create" ? "Xác nhận" : "Cập nhật"}
            </AppButton>
          </div>
        </div>
      </Modal>
    );
  },
);
